import {
  NotificationModel,
  type NetworkNotification,
  type NotificationPage
} from '@/models/notification';
import { signedApiClient } from '@/services/apiClient';

/**
 * The notification centre's API surface.
 *
 * Nothing here takes a user id — every route resolves its subject from the
 * signed request. That is deliberate: an id in the path has to be checked
 * against the session on every route, and the platform already has one endpoint
 * (`PATCH /notification/:id/markFulfilled`) that forgot to.
 */
class NotificationService {
  private notificationEndpoint = '';

  initService(notificationEndpoint: string) {
    this.notificationEndpoint = notificationEndpoint;
  }

  /**
   * One page, newest first. `cursor` is the previous page's `nextCursor`.
   *
   * The uri is built once and passed to both `signedApiClient` and `.get`,
   * because the HMAC covers the path — signing one string and requesting another
   * fails authentication in a way that looks like a permissions bug.
   */
  async getNotifications(options: {
    studyIdentifier?: string;
    cursor?: string | null;
    limit?: number;
    unreadOnly?: boolean;
  } = {}): Promise<NotificationPage> {
    const params = new URLSearchParams();
    if (options.studyIdentifier) params.set('studyId', options.studyIdentifier);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.unreadOnly) params.set('unreadOnly', 'true');

    const query = params.toString();
    const uri = query ? `${this.notificationEndpoint}?${query}` : this.notificationEndpoint;

    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);

    // Defaulted rather than assumed: an empty page that omits the field (or
    // sends null) would otherwise throw and take the whole list down.
    const rows = (response.data.notifications ?? []) as NetworkNotification[];

    return {
      notifications: rows.map(notification =>
        NotificationModel.mapFromBackend(notification)
      ),
      nextCursor: response.data.nextCursor ?? null
    };
  }

  async getUnreadCount(studyIdentifier?: string): Promise<number> {
    const uri = studyIdentifier ?
      `${this.notificationEndpoint}/unread-count?studyId=${studyIdentifier}` :
      `${this.notificationEndpoint}/unread-count`;

    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return response.data.unread ?? 0;
  }

  async markRead(id: string): Promise<void> {
    const uri = `${this.notificationEndpoint}/${id}/read`;
    const apiClient = signedApiClient('PATCH', uri);
    await apiClient.patch(uri);
  }

  async markAllRead(studyIdentifier?: string): Promise<number> {
    const uri = `${this.notificationEndpoint}/read-all`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, { studyId: studyIdentifier });
    return response.data.updated ?? 0;
  }
}

export default new NotificationService();
